/**
 * VAD Preloader Utility
 * Preloads the Silero VAD model in the background to eliminate startup delay
 * when the user clicks "Start AI Coach".
 */
import { MicVAD } from '@ricky0123/vad-web';

let preloadPromise = null;
let preloadedVad = null;

function normalizePublicUrl() {
    const raw = process.env.PUBLIC_URL;
    if (!raw) return '';
    return raw === '/' ? '' : raw.replace(/\/$/, '');
}

function getDefaultAssetConfig() {
    const base = normalizePublicUrl();
    return {
        baseAssetPath: `${base}/voice/vad/`,
        onnxWASMBasePath: `${base}/voice/ort/`,
    };
}

/**
 * Starts preloading the VAD model in the background.
 * Safe to call multiple times - returns the same promise.
 * @param {object} assetConfig - Optional custom asset paths
 * @returns {Promise<MicVAD|null>} - Resolves to the preloaded VAD instance
 */
export function startVadPreload(assetConfig = null) {
    if (preloadPromise) {
        return preloadPromise;
    }

    const config = assetConfig || getDefaultAssetConfig();

    preloadPromise = MicVAD.new({
        model: 'v5',
        startOnLoad: false,
        processorType: 'auto',
        redemptionMs: 700,
        preSpeechPadMs: 250,
        minSpeechMs: 400, // Increased from 250ms for less noise sensitivity
        submitUserSpeechOnPause: true,
        baseAssetPath: config.baseAssetPath,
        onnxWASMBasePath: config.onnxWASMBasePath,
        // Placeholder handlers - will be replaced when consumed
        onSpeechStart: () => { },
        onSpeechRealStart: () => { },
        onSpeechEnd: () => { },
    })
        .then((vad) => {
            console.log('[VAD Preload] Model preloaded successfully.');
            preloadedVad = vad;
            return vad;
        })
        .catch((err) => {
            console.warn('[VAD Preload] Failed to preload VAD model:', err);
            preloadPromise = null;
            return null;
        });

    return preloadPromise;
}

/**
 * Returns the preloaded VAD instance if available.
 * @returns {MicVAD|null}
 */
export function getPreloadedVad() {
    return preloadedVad;
}

/**
 * Clears the preloaded VAD reference after consumption.
 * Call this after transferring ownership to the component.
 */
export function clearPreloadedVad() {
    preloadedVad = null;
    preloadPromise = null;
}

/**
 * Checks if preloading has started.
 * @returns {boolean}
 */
export function isPreloadStarted() {
    return preloadPromise !== null;
}
